import { $getRoot } from 'lexical';
import { getReferencesMap } from 'modules/References/utils/getReferencesMap';
import * as Constants from 'const';
import * as DocumentsModels from 'containers/Documents/models';
import * as Models from 'models';
import { CellEntityType } from 'services/ArtboardConverter/constants';
import { Schemas } from 'services/ArtboardConverter/models';
import { getInlineStylesForExport } from 'services/ArtboardConverter/utils/getInlineStylesForExport';
import { hasToken } from 'utils/autoheight/';
import { getIntegerFromStyle } from 'utils/getIntegerFromStyle';
import { createLexicalTextEditor } from '../editors';
import { $applyBrandStyle } from '../lexicalUtils';
import { $applyReferenceCitationNumbering } from '../Plugins/ReferenceCitationPlugin';
import { ArtboardConverterStyles, convertLexicalEditorState } from './convertLexicalEditorState';

type Context = {
  projectType: Constants.ProjectType;
  colors: Models.BrandColorsList;
  fonts: Models.BrandFontsList;
  brandStyle: Models.BrandStyleMap | undefined;
  defaultBrandStyle: Models.BrandStyleMap | undefined;
  citationsOrderByDocuments: DocumentsModels.ReferenceCitationsOrderByDocumentsMap;
  surfaces: Models.ScreensOrderedMap;
};

export function createTextCellSchema(
  relation: Models.RegularRelationMap<Models.TextRelationStyles>,
  document: Models.TextComponentMap,
  context: Context,
): Schemas.Text & { isCellAutoHeight: boolean } {
  const {
    projectType, surfaces,
    colors, fonts, brandStyle, defaultBrandStyle,
    citationsOrderByDocuments,
  } = context;

  const lexicalState = document.get('lexicalState');
  if (!lexicalState) {
    return {
      entityType: CellEntityType.TEXT,
      paragraphs: [],
      isCellAutoHeight: false,
    };
  }
  const editor = createLexicalTextEditor(lexicalState);
  const isCellAutoHeight = editor.read(() => hasToken($getRoot().getTextContent()));

  const referencesMap = getReferencesMap(
    citationsOrderByDocuments,
    document?.get('referenceCitations')?.toJS() ?? [],
    relation.get('id'),
    document.get('id'),
  );
  editor.update(() => $applyReferenceCitationNumbering(referencesMap), { discrete: true });

  const brandStyleChanged = relation.getIn(['styles', 'brandStyleChanged']);
  const brandStyleId = relation.getIn(['styles', 'brandStyleId']);
  if (!brandStyleChanged && brandStyleId) {
    const brandStyleToApply = brandStyle ?? defaultBrandStyle;
    editor.update(() => {
      $applyBrandStyle(brandStyleToApply, colors, fonts);
    }, { discrete: true });
  }

  const converterOptions = {
    brandStyle, colors, fonts,
    projectType,
    surfaces,
  };
  const { fontSize, color, fontFamily } = getInlineStylesForExport(brandStyle, colors, fonts);
  const converterStyles: ArtboardConverterStyles = {
    color,
    partColor: Constants.DefaultCustomStyle.FONT_COLOR as string,
    bulletColor: '', // should be empty string,
    fontSize: fontSize ? getIntegerFromStyle(fontSize) : (Constants.DefaultCustomStyle.FONT_SIZE as number),
    fontFamily,
    lineHeight: Constants.TextLineHeightValue.ONE_POINT_FIVE,
  };

  const paragraphs = convertLexicalEditorState(editor.getEditorState(), converterOptions, converterStyles);

  return {
    entityType: CellEntityType.TEXT,
    paragraphs,
    isCellAutoHeight,
  };
}
